import { ExploreContext } from "@/components/explore/ExploreContext";
import { TableView } from "@/components/explore/ExploreTable";
import { HeatMapView } from "@/components/explore/Heatmap";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMemo, useState } from "react";

interface StationByTrackViewProps {
  trackZones: Array<{
    name: string;
    values: number[];
    apm_track_id?: number;
    apm_origin_station_id?: number;
    apm_destination_station_id?: number;
    id?: number;
    zone_ids?: number[];
  }>;
  stations: Array<{
    id: string;
    code: string;
    name: string;
  }>;
  tracks: Array<{
    id: string;
    code: string;
  }>;
  times: string[];
}

export function StationByTrackView({
  trackZones,
  stations,
  tracks,
  times,
}: StationByTrackViewProps) {
  const [exploreMode, setExploreMode] = useState("heatmap");

  const aggregatedZones = useMemo(() => {
    const result: any[] = [];

    // For each station
    stations.forEach((station) => {
      // For each track
      tracks.forEach((track) => {
        // Find segments STARTING at this station on this track (Outbound only)
        const outboundSegments = trackZones.filter(
          (zone) =>
            zone.apm_track_id?.toString() === track.id &&
            zone.apm_origin_station_id?.toString() === station.id
        );

        if (outboundSegments.length > 0) {
          // Aggregate values across time intervals
          // Usually there's only 1 outbound segment per track per station
          // But we sum just in case (e.g. junctions)
          const aggregatedValues = outboundSegments[0].values.map(
            (_, timeIndex) =>
              outboundSegments.reduce(
                (sum, seg) => sum + seg.values[timeIndex],
                0
              )
          );

          const zoneIds = outboundSegments
            .map((s) => s.id || s.zone_ids || [])
            .flat()
            .filter((id): id is number => typeof id === "number");

          result.push({
            id: undefined, // Aggregate zone doesn't have single DB ID
            name: `${station.code} - ${track.code} PAX`,
            values: aggregatedValues,
            notes: [],
            concerns: [],
            zone_ids: zoneIds,
          });
        }
      });
    });

    return result;
  }, [trackZones, stations, tracks]);

  if (aggregatedZones.length === 0) {
    return (
      <div className="text-slate-400 text-center py-12">
        No data available for current selection
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center">
        <span className="text-sm mr-2">View as:</span>
        <Tabs value={exploreMode} onValueChange={setExploreMode}>
          <TabsList>
            <TabsTrigger value="heatmap">Heatmap</TabsTrigger>
            <TabsTrigger value="table">Table</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <ExploreContext.Provider
        value={{
          zones: aggregatedZones,
          times: times,
          concerns: [],
          zoneType: "apm_station",
        }}
      >
        {exploreMode === "heatmap" && (
          <HeatMapView measure="pax" height={600} onCellClick={() => {}} />
        )}
        {exploreMode === "table" && <TableView onCellClick={() => {}} />}
      </ExploreContext.Provider>
    </div>
  );
}
